// react
import { useEffect, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';
// i18n
import { useTranslation } from 'react-i18next';

// types
import { ActionButton, LoadingStatus, TransactionType } from '../../../@types';

// ui
import { AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// forms
import { FormAddTransaction } from '../../forms/FormAddTransaction/FormAddTransaction';

// props
export interface DialogAddTransactionProps {
	DialogActionsProps?: Partial<DialogActionsProps>;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	type?: TransactionType;
}

export const DialogAddTransaction = ({
	DialogActionsProps = {},
	isOpen,
	onClose,
	onSuccess,
	type,
}: DialogAddTransactionProps) => {
	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');

	const handleClose = () => {
		if (onSuccess && status === 'SUCCESS') {
			onSuccess();
		}

		if (onClose) {
			onClose();
		}
	};

	DialogActionsProps.actions = [];

	if (['DEFAULT', 'ERROR'].includes(status)) {
		DialogActionsProps.actions.push({
			children: t('cancel'),
			onClick: handleClose,
		});
	}

	DialogActionsProps.title = {
		children: t('recordPayment'),
		variant: 'h3',
	};

	const actionSubmit: ActionButton = {
		children: t('recordPayment'),
		disabled: Boolean(
			feedback?.items?.some((item) =>
				[
					'TRANSACTION_CONTRACT_STEP_ID_NON_EXISTENT',
					'TRANSACTION_CATEGORY_ID_NON_EXISTENT',
				].includes(String(item.id))
			)
		),
		form: 'form-add-payment',
		loading: status === 'LOADING',
		type: 'submit',
		variant: 'contained',
	};

	if (type === 'CASH_BENEFIT') {
		actionSubmit.children = t('recordCashBenefit');

		if (DialogActionsProps.title) {
			DialogActionsProps.title.children = t('recordCashBenefit');
		}
	}

	if (type === 'DIRECT') {
		actionSubmit.children = t('recordDirectPayment');

		if (DialogActionsProps.title) {
			DialogActionsProps.title.children = t('recordDirectPayment');
		}

		if (paramDialog === 'initiateDirectPayment') {
			actionSubmit.children = t('initiateDirectPayment');

			if (DialogActionsProps.title) {
				DialogActionsProps.title.children = t('initiateDirectPayment');
			}
		}
	}

	if (type === 'OPEX') {
		actionSubmit.children = t('recordOpEx');

		if (DialogActionsProps.title) {
			DialogActionsProps.title.children = t('recordOpEx');
		}
	}

	if (DialogActionsProps.actions) {
		DialogActionsProps.actions.push(actionSubmit);
	}

	if (status === 'SUCCESS') {
		DialogActionsProps.actions = [
			{
				children: t('done'),
				onClick: handleClose,
				variant: 'contained',
			},
		];
	}

	// reset status when dialog is closed
	useEffect(() => {
		if (!isOpen) {
			setStatus('DEFAULT');
		}
	}, [isOpen]);

	return (
		<DialogActions
			hasClose={true}
			isOpen={isOpen}
			maxWidth={570}
			onClose={handleClose}
			{...DialogActionsProps}>
			<FormAddTransaction
				onFeedback={setFeedback}
				setStatus={setStatus}
				status={status}
				type={type}
			/>
		</DialogActions>
	);
};
